import { useSelector } from "react-redux";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import { TagPicker } from "components/tags";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getFilteredTags, mapTags } from "helpers/tags";
import { Author, EventType, ITicket } from "interfaces/entities/ticket";
import { selectFeatureToggleTagSuggestions } from "store/entities/license";
import { ITag } from "store/entities/tags";

import { SectionWrapper } from "../styles";

import { AiTagSuggestions } from "./AITagSuggestions";

interface IProps {
  ticket: ITicket;
  tags: ITag[];
  allTags: ITag[];
  canManageTicket: boolean;
  tagsUsageAllowed: boolean;
  onTagAdd(tagId: string, suggested: boolean): void;
  onTagRemove(tagId: string): void;
  opened: boolean;
  onHeaderClick(): void;
}

export const TicketDetailsTags: React.FC<IProps> = ({
  ticket,
  tags,
  allTags,
  canManageTicket,
  tagsUsageAllowed,
  onTagRemove,
  opened,
  onHeaderClick,
  onTagAdd,
}) => {
  const featureToggleTagSuggestions = useSelector(selectFeatureToggleTagSuggestions);
  const messageEvents = ticket.events.filter(e => e.type === EventType.Message);
  const isNewlyCreatedByAgent = messageEvents.length === 1 && messageEvents[0]?.author?.type === Author.Agent;
  const filteredTags = tags.filter(tag => tag.teamId === ticket.assignment.team.id || tag.teamId === null);

  const displayTagSuggestions = canManageTicket && featureToggleTagSuggestions;

  const handleTagSelect = (tagId: string, suggested: boolean): void => {
    onTagAdd(tagId, suggested);
    trackEngagement("Tickets: Tag added", EventPlace.Tickets, { from: "Ticket details", suggested });
  };

  const handleAddTagClick = (): void => {
    trackEngagement("Tickets: Add tag button clicked", EventPlace.Tickets, { from: "Ticket details" });
  };

  return (
    <SectionWrapper borderTop>
      <AccordionItem title="Tags" opened={opened} onHeaderClick={onHeaderClick}>
        {displayTagSuggestions && <AiTagSuggestions onTagAdd={tagId => handleTagSelect(tagId, true)} />}
        <TagPicker
          options={mapTags(filteredTags, tagsUsageAllowed)}
          selectedTags={getFilteredTags(allTags, ticket.tagIds)}
          canManageTags={canManageTicket}
          onRemove={onTagRemove}
          onSelect={tagId => handleTagSelect(tagId, false)}
          onAddTagClick={handleAddTagClick}
          ticketID={ticket.id}
          teamID={ticket.assignment.team.id}
          isNewTicketCreatedByAgentOpened={isNewlyCreatedByAgent}
        />
      </AccordionItem>
    </SectionWrapper>
  );
};
